import React, { useState } from 'react';
import {
  Box,
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';

import {
  Container,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ButtonRow,
  CancelButton,
  ConfirmButton
} from './styles';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderMultiSectionDigitalClockTimeView } from '@mui/x-date-pickers/timeViewRenderers';

interface ExtendShiftProps {
  onCancel: () => void;
  onExtend: (shiftTime: string) => void;
  loading?: boolean;
  shiftEndTime?: string;
}

const ExtendShift: React.FC<ExtendShiftProps> = ({
  onCancel,
  onExtend,
  loading = false,
  shiftEndTime
}) => {
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(
    shiftEndTime ? dayjs(shiftEndTime) : null
  );
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = () => {
    if (!selectedDate) {
      setError(true);
      setErrorMessage("Extended time is required");
      return;
    }

    const start = dayjs(shiftEndTime);

    // Check if the selected date (down to minute) is strictly after the current shift end (down to minute)
    // If they are the same minute, it's invalid (no extension).
    if (selectedDate.diff(start, 'minute') <= 0) {
      setError(true);
      setErrorMessage("Extended time must be after the current shift end time");
      return;
    }

    onExtend(selectedDate.format('YYYY-MM-DD HH:mm:ss'));
  };

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    setSelectedDate(prev => {
      if (!prev) return newDate;
      if (!newDate) return null;
      // Keep the time from previous selection, apply new date
      return prev.year(newDate.year()).month(newDate.month()).date(newDate.date());
    });
    setError(false);
  };

  const handleTimeChange = (newTime: dayjs.Dayjs | null) => {
    setSelectedDate(prev => {
      if (!prev) return newTime;
      if (!newTime) return null;
      // Keep the date from previous selection, apply new time
      return prev.hour(newTime.hour()).minute(newTime.minute());
    });
    setError(false);
  };

  const shiftEndDayjs = shiftEndTime ? dayjs(shiftEndTime) : dayjs();
  const isSameDay = selectedDate ? selectedDate.isSame(shiftEndDayjs, 'day') : false;

  return (
    <Container>
      <ModalHeader>
        <ModalTitle>Extend Shift</ModalTitle>
        <ModalSubtitle>Please select the new end date and time for the shift.</ModalSubtitle>
      </ModalHeader>

      <Box display="flex" flexDirection="column" gap={3}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box component="label" sx={{ display: 'block', mb: 1, fontWeight: 500, fontSize: '0.9rem', color: '#455A64' }}>
                New End Date<span style={{ color: 'red' }}>*</span>
              </Box>
              <Box sx={{ position: 'relative' }}>
                <DatePicker
                  value={selectedDate}
                  onChange={handleDateChange}
                  minDate={shiftEndDayjs}
                  format="DD/MM/YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: error,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: error ? 'red' : '#E0E0E0' }
                        }
                      }
                    },
                    popper: {
                      disablePortal: true,
                      sx: {
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        transform: 'none !important',
                        zIndex: 1300,
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box component="label" sx={{ display: 'block', mb: 1, fontWeight: 500, fontSize: '0.9rem', color: '#455A64' }}>
                New End Time<span style={{ color: 'red' }}>*</span>
              </Box>
              <Box sx={{ position: 'relative' }}>
                <TimePicker
                  value={selectedDate}
                  onChange={handleTimeChange}
                  minTime={isSameDay ? shiftEndDayjs : undefined}
                  ampm={false}
                  viewRenderers={{
                    hours: renderMultiSectionDigitalClockTimeView,
                    minutes: renderMultiSectionDigitalClockTimeView,
                    seconds: renderMultiSectionDigitalClockTimeView,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: error,
                      helperText: error ? errorMessage : '',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          '& fieldset': { borderColor: error ? 'red' : '#E0E0E0' }
                        }
                      }
                    },
                    popper: {
                      disablePortal: true,
                      sx: {
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        transform: 'none !important',
                        zIndex: 1300,
                      }
                    }
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ p: 1, bgcolor: '#f5f7fa', borderRadius: 2 }}>
            <Box component="span" sx={{ fontSize: '0.85rem', color: '#666' }}>
              Current Shift End:
            </Box>
            <Box component="span" sx={{ ml: 1, fontWeight: 600, fontSize: '0.85rem' }}>
              {shiftEndDayjs.format('DD/MM/YYYY HH:mm')}
            </Box>
          </Box>

        </LocalizationProvider>
      </Box>

      <ButtonRow>
        <CancelButton variant="outlined" onClick={onCancel} disabled={loading}>
          Cancel
        </CancelButton>
        <ConfirmButton variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Extending...' : 'Extend Shift'}
        </ConfirmButton>
      </ButtonRow>
    </Container>
  );
};

export default ExtendShift;
